// filepath: apps/web/src/admin/counterparties/list.tsx
//
// FR-Group-B — CounterpartyListScreen (ISS-442 UX slim).
//
// The 4th of the four design-system screens (per
// docs/design/FR-Group-B/manifest.json). Lists every active
// counterparty in the current tenant with a "New counterparty"
// CTA, search-by-name filter, and a side-panel link to the
// detail view.
//
// ISS-442 BA spec (verbatim):
//   (a) Keep three columns in the list: name, status, last update.
//       Move legal_form and tax_id to the detail and edit views.
//   (b) Translation all locales (RU authoritative, KK falls back
//       to RU when a key is missing, EN must have real English).
//       No hardcoded strings. i18n namespace
//       `directory.counterparties.*`.
//   (c) Date format `dd-MM-yyyy HH:mm` in the list + detail view,
//       through ONE shared formatter in the user's browser
//       timezone. Lowercase `yyyy` only.
//
// INV-2: PII fields (tax_id / email / phone / address) are NOT
// rendered in the list (they were the slim target). The detail +
// edit views surface them under the same INV-2 contract: the
// server is the only authority on what the caller may see, the
// SPA MUST NOT attempt to PII-decode locally.
//
// INV-9: no `dangerouslySetInnerHTML` anywhere — every dynamic
// value flows through JSX text nodes or the `Card` / `Badge`
// components from `@/design-system`.

import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/design-system/Card";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { useTranslation } from "@/i18n/useTranslation";
import { formatDatePattern } from "@/i18n/format";
import { navigate } from "@/features/cabinet/usePathname";
import { ApiError } from "@/definitions/types";
import {
  Counterparty,
  LegalForm,
  LEGAL_FORMS,
  listCounterparties,
} from "./api";

/**
 * ISS-443 acceptance check 2: classify the thrown error so the
 * list screen can render <PermissionDenied /> (no React crash) for
 * unprivileged viewers, per BA close-condition. ISS-448 round 2
 * (B2, architect note_13946): the classifier checks the TYPED
 * `ApiError` fields (`.status` + `.code`), not string substrings.
 * The pre-round-2 string-substring classifier was unreliable
 * because the real `callApi` throw path produced bare
 * "HTTP 403" / "HTTP 409" messages (no `forbidden` /
 * `not_found` / `tax_id_conflict` / `row_version_mismatch`
 * substring to match). The typed check is robust against any
 * server-side change to the error message body.
 */
function isPermissionDenied(e: unknown): boolean {
  if (!e) return false;
  if (e instanceof ApiError) {
    return e.status === 403 || e.status === 404;
  }
  return false;
}

/**
 * ISS-431-uatrzt2 React crash fix: the project explicitly opts out
 * of `react-router-dom` (FR-UI-CABINET-1 AC-9) and uses its own
 * `usePathname()` / `navigate()` primitives (see
 * `apps/web/src/features/cabinet/usePathname.ts`). The previous
 * `Link` import from `react-router-dom` threw
 * `Cannot destructure property 'basename' of 'k.useContext(...)' as
 * it is null` on every render because no `<Router>` ancestor wraps
 * the production app. The fix below replaces the router Link
 * with a plain `<a href>` whose onClick calls `navigate(href)` on
 * left-click-without-modifier (matching the cabinet's
 * `AdminSection` pattern at line 115-125).
 */
function handleSpaNavigate(
  e: React.MouseEvent<HTMLAnchorElement>,
  href: string,
): void {
  if (e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) {
    return;
  }
  e.preventDefault();
  navigate(href);
}

export interface CounterpartyListScreenProps {
  /** Optional initial filter — typically from URL search params. */
  initialQuery?: string;
  /**
   * Render-prop override for tests. Defaults to fetching from the
   * canonical REST endpoint.
   */
  fetcher?: (opts: {
    q?: string;
    legal_form?: LegalForm;
    status?: "active" | "removed";
  }) => Promise<Counterparty[]>;
}

export function CounterpartyListScreen({
  initialQuery = "",
  fetcher,
}: CounterpartyListScreenProps): React.ReactElement {
  const { t, locale } = useTranslation();
  const [q, setQ] = useState<string>(initialQuery);
  const [legalForm, setLegalForm] = useState<LegalForm | "">("");
  const [rows, setRows] = useState<Counterparty[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  // ISS-448 round 2 (B2): the error state is the TYPED thrown
  // value (preserved so the typed classifier can inspect
  // `.status` + `.code`). The pre-round-2 code stored only
  // `e.message` (a string); the typed classifier checks
  // `e instanceof ApiError`, so storing the bare string
  // returned false even when the throw was a real ApiError.
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const f =
        fetcher ??
        (() =>
          listCounterparties({
            q: q || undefined,
            legal_form: legalForm || undefined,
            status: "active",
          }));
      const data = await f({
        q: q || undefined,
        legal_form: legalForm || undefined,
        status: "active",
      });
      setRows(data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [fetcher, q, legalForm]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section data-testid="counterparty-list-screen">
      <header className="counterparty-list-header">
        <h1 id="counterparty-list-title">
          {t("directory.counterparties.list.title", "Counterparties")}
        </h1>
        <a
          href="/admin/counterparties/new"
          data-testid="counterparty-list-new-link"
          onClick={(e) => handleSpaNavigate(e, "/admin/counterparties/new")}
          style={{ textDecoration: "none" }} // design-adherence-ignore: link reset (button-styled anchor; no underline)
        >
          <Button variant="primary">
            {t("directory.counterparties.list.new", "New counterparty")}
          </Button>
        </a>
      </header>

      <Card>
        <div className="counterparty-list-filterbar">
          <label htmlFor="counterparty-list-q">
            {t(
              "directory.counterparties.list.search_by_name",
              "Search by name",
            )}
          </label>
          <input
            id="counterparty-list-q"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="counterparty-list-q"
          />
          <label htmlFor="counterparty-list-legal-form">
            {t("directory.counterparties.list.legal_form_filter", "Legal form")}
          </label>
          <select
            id="counterparty-list-legal-form"
            value={legalForm}
            onChange={(e) => setLegalForm(e.target.value as LegalForm | "")}
            data-testid="counterparty-list-legal-form"
          >
            <option value="">
              {t("directory.counterparties.list.legal_form_all", "All")}
            </option>
            {LEGAL_FORMS.map((lf) => (
              <option key={lf} value={lf}>
                {t(`directory.counterparties.legal_form.${lf}`, lf)}
              </option>
            ))}
          </select>
          <Button
            onClick={() => void load()}
            data-testid="counterparty-list-refresh"
          >
            {t("directory.counterparties.list.refresh", "Refresh")}
          </Button>
        </div>
      </Card>

      {loading ? (
        <p data-testid="counterparty-list-loading">
          {t(
            "directory.counterparties.list.loading",
            "Loading counterparties…",
          )}
        </p>
      ) : error && isPermissionDenied(error) ? (
        // ISS-443 acceptance check 2: unprivileged viewers see
        // <PermissionDenied />, NOT a React crash and NOT a
        // generic error message. The component itself is
        // role="alert" so screen readers announce it.
        <div data-testid="counterparty-list-permission-denied">
          <PermissionDenied />
        </div>
      ) : error ? (
        <p role="alert" data-testid="counterparty-list-error">
          {t(
            "directory.counterparties.list.error",
            "Failed to load counterparties: {{message}}",
            {
              message:
                error instanceof Error
                  ? error.message
                  : typeof error === "string"
                    ? error
                    : String(error),
            },
          )}
        </p>
      ) : rows.length === 0 ? (
        <p data-testid="counterparty-list-empty">
          {t(
            "directory.counterparties.list.empty",
            "No counterparties match the current filter.",
          )}
        </p>
      ) : (
        <table aria-labelledby="counterparty-list-title">
          <caption className="visually-hidden">
            {/* ISS-442: list shows name, status, last update only */}
            {t("directory.counterparties.list.title", "Counterparties")}
          </caption>
          <thead>
            <tr>
              <th scope="col">
                {t("directory.counterparties.list.column.name", "Name")}
              </th>
              <th scope="col">
                {t("directory.counterparties.list.column.status", "Status")}
              </th>
              <th scope="col">
                {t(
                  "directory.counterparties.list.column.last_update",
                  "Last update",
                )}
              </th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} data-testid={`counterparty-row-${c.id}`}>
                <td>{c.name}</td>
                <td>
                  <Badge tone={c.status === "active" ? "success" : "neutral"}>
                    {t(`directory.counterparties.status.${c.status}`, c.status)}
                  </Badge>
                </td>
                <td>{formatDatePattern(new Date(c.updated_at), locale)}</td>
                <td>
                  <a
                    href={`/admin/counterparties/${encodeURIComponent(c.id)}`}
                    data-testid={`counterparty-row-${c.id}-detail`}
                    onClick={(e) =>
                      handleSpaNavigate(
                        e,
                        `/admin/counterparties/${encodeURIComponent(c.id)}`,
                      )
                    }
                  >
                    {t("directory.counterparties.list.view", "View")}
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default CounterpartyListScreen;
